import { PrinterOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  Radio,
  Table,
  type TableColumnsType,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStockAdjust, useStockGetBatch } from '@/api/generated/endpoints/stock/stock';
import type { AdjustmentCreateKind, BatchDetail, MovementOut } from '@/api/generated/model';
import { useHasRole } from '@/auth/useHasRole';
import { ExpiryTag } from '@/components/ExpiryTag';
import { confirmAction } from '@/components/confirmAction';
import { QtyCell, QtyInput } from '@/components/qty';
import { problemMessage } from '@/i18n/problem';
import { formatDecimal, mulDecimals, sumDecimals } from '@/lib/decimal';
import { applyProblem } from '@/lib/problemForm';
import { qtyRule, type QtyValue } from '@/lib/qty';
import { invalidatePath } from '@/lib/queryCache';
import { qtyText } from '@/lib/units';
import { BatchShipments } from '@/pages/stock/BatchShipments';
import { LabelPrintModal } from '@/pages/stock/LabelPrintModal';

const DATE = 'DD.MM.YYYY';
const DATETIME = 'DD.MM.YYYY HH:mm';

/**
 * Карточка партии: реквизиты, резервы и отгрузки, журнал движений, списание (с кладовщика)
 * и инвентаризация (менеджер). `pallet` — номер палета, если пришли по QR его этикетки.
 */
export function BatchDrawer({
  batchId,
  pallet,
  fullWidth = false,
  onClose,
}: {
  batchId: number | null;
  pallet?: number | null;
  /** Телефон: панель на весь экран — по QR палета её открывают у палета (T-176). */
  fullWidth?: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const detail = useStockGetBatch(batchId ?? 0, { query: { enabled: batchId !== null } });
  const b = detail.data;
  return (
    <Drawer
      title={batchId ? t('batch.title', { id: batchId }) : ''}
      open={batchId !== null}
      onClose={onClose}
      width={fullWidth ? '100%' : 640}
      loading={detail.isPending && batchId !== null}
      destroyOnHidden
    >
      {b && <BatchView batch={b} pallet={pallet ?? null} />}
    </Drawer>
  );
}

function BatchView({ batch: b, pallet }: { batch: BatchDetail; pallet: number | null }) {
  const { t } = useTranslation();
  // Списание — с кладовщика (решение бизнеса 2026-10-21), инвентаризация — с менеджера.
  const canAdjust = useHasRole('storekeeper');
  const [printing, setPrinting] = useState(false);
  const uom = b.product.base_uom;

  const columns: TableColumnsType<MovementOut> = [
    {
      key: 'date',
      title: t('batch.colDate'),
      dataIndex: 'created_at',
      width: 140,
      render: (d: string) => dayjs(d).format(DATETIME),
    },
    {
      key: 'type',
      title: t('batch.colType'),
      dataIndex: 'type',
      render: (type: string) => t(`movement.${type}`),
    },
    {
      key: 'qty',
      title: t('batch.colQty'),
      align: 'right',
      width: 130,
      render: (_: unknown, m) => <QtyCell value={m.qty} uom={uom} />,
    },
    {
      key: 'comment',
      title: t('batch.colComment'),
      dataIndex: 'comment',
      render: (c: string | null) => c ?? '—',
    },
  ];

  return (
    <>
      {pallet !== null && b.pallets !== null && pallet <= b.pallets && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('batch.scannedPallet', { no: pallet, total: b.pallets, id: b.id })}
        />
      )}
      {/* Все размеры явно: пропущенные antd считает тремя колонками — в панели шириной
          640 px значение «Принята» сжималось до одной буквы. */}
      <Descriptions size="small" bordered column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}>
        <Descriptions.Item label={t('stock.colProduct')} span={2}>
          {b.product.name}
        </Descriptions.Item>
        <Descriptions.Item label={t('batch.qtyAvailable')}>
          <QtyCell value={b.qty_available} uom={uom} />
        </Descriptions.Item>
        <Descriptions.Item label={t('batch.qtyReceived')}>
          <QtyCell value={b.qty_received} uom={uom} />
        </Descriptions.Item>
        <Descriptions.Item label={t('batch.received')}>
          {dayjs(b.received_on).format(DATE)}
        </Descriptions.Item>
        <Descriptions.Item label={t('batch.expires')}>
          <ExpiryTag expiresOn={b.expires_on} days={b.days_left} />
          {b.expiry_source && ` ${t(`receiving.expirySource.${b.expiry_source}`)}`}
        </Descriptions.Item>
        <Descriptions.Item label={t('batch.pallets')} span={2}>
          {b.pallets ? `${b.pallets} · ${b.pallet_codes.join(', ')}` : t('batch.noPallets')}
        </Descriptions.Item>
        {b.product.base_uom === 'box' && (
          <Descriptions.Item label={t('batch.boxWeight')} span={2}>
            {b.box_weight_kg
              ? t('batch.boxWeightValue', {
                  kg: formatDecimal(b.box_weight_kg, { maxPlaces: 3 }),
                  total: formatDecimal(mulDecimals(b.qty_available, b.box_weight_kg), {
                    maxPlaces: 1,
                  }),
                })
              : t('batch.boxWeightUnknown')}
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t('batch.country')}>{b.country ?? '—'}</Descriptions.Item>
        <Descriptions.Item label={t('batch.caliber')}>{b.caliber ?? '—'}</Descriptions.Item>
        <Descriptions.Item label={t('batch.supplier')} span={2}>
          {b.supplier ?? '—'}
        </Descriptions.Item>
        {b.comment && (
          <Descriptions.Item label={t('batch.comment')} span={2}>
            {b.comment}
          </Descriptions.Item>
        )}
      </Descriptions>

      {b.pallets !== null && (
        <Button
          icon={<PrinterOutlined />}
          style={{ marginTop: 12 }}
          onClick={() => {
            setPrinting(true);
          }}
        >
          {t('labels.button', { count: b.pallets })}
        </Button>
      )}
      {printing && b.pallets !== null && (
        <LabelPrintModal
          batch={{ id: b.id, pallets: b.pallets, product: b.product.name }}
          onClose={() => {
            setPrinting(false);
          }}
        />
      )}
      <Divider orientation="left">{t('batchShip.title')}</Divider>
      <BatchShipments batch={b} />
      <Divider orientation="left">{t('batch.movements')}</Divider>
      <Table<MovementOut>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={b.movements}
        pagination={false}
      />

      {canAdjust && (
        <>
          <Divider orientation="left">{t('adjust.title')}</Divider>
          <AdjustForm batch={b} />
        </>
      )}
    </>
  );
}

interface AdjustValues {
  kind: AdjustmentCreateKind;
  qty: QtyValue;
  comment: string;
}

function AdjustForm({ batch }: { batch: BatchDetail }) {
  const { t } = useTranslation();
  const canCorrect = useHasRole('manager');
  const [form] = Form.useForm<AdjustValues>();
  const [error, setError] = useState<string | null>(null);
  const kind = Form.useWatch('kind', form) as AdjustmentCreateKind | undefined;
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();
  const adjust = useStockAdjust({
    mutation: {
      onSuccess: async (out) => {
        form.resetFields();
        void message.success(t('adjust.done', { qty: out.qty_available }));
        await Promise.all([
          invalidatePath(queryClient, '/api/v1/batches'),
          invalidatePath(queryClient, '/api/v1/stock'),
        ]);
      },
      onError: (e) => {
        if (!applyProblem(form, e, { insufficient_stock: 'qty' })) setError(problemMessage(e));
      },
    },
  });

  async function submit(v: AdjustValues) {
    setError(null);
    if (typeof v.qty !== 'string') return;
    // Списание вводится положительным «сколько списать», в API уходит со знаком минус.
    const delta = v.kind === 'writeoff' ? `-${v.qty}` : v.qty;
    const uom = batch.product.base_uom;
    const qty = qtyText(t, v.qty, uom);
    const ok = await confirmAction(modal, {
      title: t(
        v.kind === 'writeoff' ? 'adjust.confirmWriteoffTitle' : 'adjust.confirmCorrectionTitle',
      ),
      details: [
        t('adjust.confirmBatch', { product: batch.product.name, id: batch.id }),
        t(v.kind === 'writeoff' ? 'adjust.confirmQtyWriteoff' : 'adjust.confirmQtyCorrection', {
          qty,
        }),
        t('adjust.confirmReason', { reason: v.comment.trim() }),
        t('adjust.confirmAfter', {
          qty: qtyText(t, sumDecimals([batch.qty_available, delta]), uom),
        }),
      ],
      okText: t(v.kind === 'writeoff' ? 'adjust.confirmWriteoff' : 'adjust.confirmCorrection', {
        qty,
      }),
      cancelText: t('common.cancel'),
    });
    if (!ok) return;
    adjust.mutate({
      batchId: batch.id,
      data: { kind: v.kind, qty_delta: delta, comment: v.comment.trim() },
    });
  }

  return (
    <Form<AdjustValues>
      form={form}
      layout="vertical"
      initialValues={{ kind: 'writeoff', qty: null }}
      onFinish={(v) => {
        void submit(v);
      }}
    >
      <Form.Item name="kind" label={t('adjust.kind')}>
        <Radio.Group
          optionType="button"
          options={[
            { label: t('adjust.writeoff'), value: 'writeoff' },
            ...(canCorrect ? [{ label: t('adjust.correction'), value: 'correction' }] : []),
          ]}
          onChange={() => {
            form.setFieldValue('qty', null);
          }}
        />
      </Form.Item>
      <Form.Item
        name="qty"
        label={kind === 'correction' ? t('adjust.qtyCorrection') : t('adjust.qtyWriteoff')}
        extra={kind === 'correction' ? t('adjust.qtyCorrectionHint') : undefined}
        rules={[qtyRule({ required: true, positive: kind !== 'correction' })]}
      >
        <QtyInput
          uom={batch.product.base_uom}
          allowNegative={kind === 'correction'}
          style={{ width: 200 }}
        />
      </Form.Item>
      <Form.Item
        name="comment"
        label={t('adjust.comment')}
        rules={[{ required: true, whitespace: true, min: 3, message: t('adjust.commentRequired') }]}
      >
        <Input.TextArea maxLength={2000} autoSize={{ minRows: 1, maxRows: 4 }} />
      </Form.Item>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Button
        type="primary"
        danger={kind === 'writeoff'}
        htmlType="submit"
        loading={adjust.isPending}
      >
        {t('adjust.submit')}
      </Button>
    </Form>
  );
}
